import { Phone, Mail, MapPin, Trash2, PhoneCall, Undo2 } from "lucide-react";

const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const Chip = ({ label, value }) =>
    value ? (
        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider border border-brand-line rounded-full px-3 py-1 text-brand-sage">
            <span className="text-brand-moss">{label}</span>
            <span className="text-brand-cream normal-case tracking-normal">{value}</span>
        </span>
    ) : null;

const QuoteCard = ({ quote, onDelete, onToggleCalled, deleting }) => {
    const tel = `tel:${quote.phone.replace(/\D/g, "")}`;
    const called = Boolean(quote.called_at);
    const overdue = !called && Date.now() - new Date(quote.created_at).getTime() > 24 * 3600 * 1000;
    return (
        <article
            data-testid={`quote-card-${quote.id}`}
            className={`rounded-lg border bg-brand-surface p-6 sm:p-7 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 ${
                called ? "border-brand-line opacity-75" : "border-brand-line"
            }`}
        >
            <div className="min-w-0 space-y-4">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                    <h3 className="font-display font-black uppercase text-brand-cream text-2xl tracking-tight" data-testid="quote-card-name">
                        {quote.name}
                    </h3>
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss">
                        {formatDate(quote.created_at)}
                    </span>
                    {overdue && (
                        <span
                            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber border border-brand-amber/40 rounded-full px-2.5 py-0.5"
                            data-testid="quote-card-overdue-badge"
                        >
                            Waiting 24h+
                        </span>
                    )}
                    {called && (
                        <span
                            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#75E29A] border border-[#75E29A]/40 rounded-full px-2.5 py-0.5"
                            data-testid="quote-card-called-badge"
                        >
                            <PhoneCall className="w-3 h-3" />
                            Called {formatDate(quote.called_at)}
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                    <a href={tel} className="flex items-center gap-2 text-brand-amber font-semibold hover:underline" data-testid="quote-card-phone">
                        <Phone className="w-4 h-4" />
                        {quote.phone}
                    </a>
                    {quote.email && (
                        <a href={`mailto:${quote.email}`} className="flex items-center gap-2 text-brand-sage hover:text-brand-amber transition-colors break-all">
                            <Mail className="w-4 h-4 shrink-0" />
                            {quote.email}
                        </a>
                    )}
                    {quote.address && (
                        <span className="flex items-center gap-2 text-brand-sage">
                            <MapPin className="w-4 h-4 shrink-0" />
                            {quote.address}
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap gap-2">
                    <Chip label="Size" value={quote.stump_size} />
                    <Chip label="Access" value={quote.situation} />
                    <Chip label="Cleanup" value={quote.cleanup} />
                </div>

                {quote.message && (
                    <p className="text-brand-sage text-sm leading-relaxed border-l-2 border-brand-amber/50 pl-4 whitespace-pre-wrap">
                        {quote.message}
                    </p>
                )}
            </div>

            <div className="flex flex-wrap lg:flex-col gap-2 lg:items-end">
                <a
                    href={tel}
                    className="inline-flex items-center justify-center gap-2 bg-brand-amber text-brand-ink font-bold text-sm px-5 py-2.5 rounded-full hover:bg-brand-amberHover transition-colors"
                    data-testid="quote-card-call-btn"
                >
                    <Phone className="w-4 h-4" />
                    Call
                </a>
                <button
                    onClick={() => onToggleCalled(quote.id, !called)}
                    disabled={deleting}
                    className={`inline-flex items-center justify-center gap-2 border text-sm px-5 py-2.5 rounded-full transition-colors disabled:opacity-50 ${
                        called
                            ? "border-brand-line text-brand-sage hover:border-brand-amber hover:text-brand-amber"
                            : "border-[#75E29A]/50 text-[#75E29A] hover:bg-[#75E29A] hover:text-brand-ink"
                    }`}
                    data-testid="quote-card-called-btn"
                >
                    {called ? <Undo2 className="w-4 h-4" /> : <PhoneCall className="w-4 h-4" />}
                    {called ? "Undo" : "Mark called"}
                </button>
                <button
                    onClick={() => onDelete(quote.id)}
                    disabled={deleting}
                    className="inline-flex items-center justify-center gap-2 border border-brand-line text-brand-sage text-sm px-5 py-2.5 rounded-full hover:border-[#ff9b7a] hover:text-[#ff9b7a] transition-colors disabled:opacity-50"
                    data-testid="quote-card-delete-btn"
                >
                    <Trash2 className="w-4 h-4" />
                    Clear
                </button>
            </div>
        </article>
    );
};

export default QuoteCard;
